/**
 * Heartfelt Gifts seasonal discovery controller.
 *
 * Seasonal collections remain published and directly accessible for SEO and
 * campaign links. This controller limits their promotional discovery and
 * Gift Finder visibility to the configured UK trading windows.
 */
(() => {
  'use strict';

  if (window.__HFSeasonalVisibilityControllerLoaded) return;
  window.__HFSeasonalVisibilityControllerLoaded = true;

  const CONFIG_SELECTOR = '[data-hf-seasonal-schedule]';
  const MANAGED_ATTRIBUTE = 'data-hf-seasonal-managed';
  const SAFE_DISCOVERY_SCOPES = [
    '.header__inline-menu',
    '#menu-drawer',
    '.hf-browse',
    '.hf-directory',
    '.hf-sub',
    '[data-hf-seasonal-discovery]',
  ];
  const DISCOVERY_ROOT_SELECTOR = [...SAFE_DISCOVERY_SCOPES, 'gift-finder'].join(', ');
  const MANAGED_ROOT_SELECTOR = ['[data-hf-season]', DISCOVERY_ROOT_SELECTOR].join(', ');
  const SEASON_NAMES = ['easter', 'halloween', 'christmas'];
  const DEFAULT_CONFIG = {
    timezone: 'Europe/London',
    hideInactiveProducts: true,
    designMode: false,
    previewDate: '',
    campaignMode: 'auto',
    campaignCutoffs: {},
    christmasLeadStart: '11-01',
    seasons: {
      easter: { mode: 'auto', leadDays: 56, endOffsetDays: 1 },
      halloween: { mode: 'auto', start: '09-01', end: '10-31' },
      christmas: { mode: 'auto', start: '09-01', end: '12-26' },
    },
  };

  let configuration = structuredCloneSafe(DEFAULT_CONFIG);
  let state = null;
  let observer = null;
  const pendingApplyRoots = new Set();
  let applyFrame = 0;

  function structuredCloneSafe(value) {
    return JSON.parse(JSON.stringify(value));
  }

  function normalize(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/\u00a0/g, ' ')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function validMode(value) {
    return ['auto', 'always_show', 'always_hide'].includes(value) ? value : 'auto';
  }

  function parseIsoDate(value) {
    const match = String(value || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return null;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const date = new Date(Date.UTC(year, month - 1, day));
    if (
      date.getUTCFullYear() !== year
      || date.getUTCMonth() !== month - 1
      || date.getUTCDate() !== day
    ) return null;
    return { year, month, day };
  }

  function parseMonthDay(value, fallback) {
    const match = String(value || '').trim().match(/^(\d{2})-(\d{2})$/);
    if (!match) return fallback;
    const month = Number(match[1]);
    const day = Number(match[2]);
    const probe = new Date(Date.UTC(2024, month - 1, day));
    if (probe.getUTCMonth() !== month - 1 || probe.getUTCDate() !== day) return fallback;
    return { month, day };
  }

  function calendarKey(value) {
    return (value.year * 10000) + (value.month * 100) + value.day;
  }

  function monthDayKey(value) {
    return (value.month * 100) + value.day;
  }

  function addDays(value, amount) {
    const date = new Date(Date.UTC(value.year, value.month - 1, value.day + Number(amount || 0)));
    return {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth() + 1,
      day: date.getUTCDate(),
    };
  }

  function formatIsoDate(value) {
    const pad = (number) => String(number).padStart(2, '0');
    return `${value.year}-${pad(value.month)}-${pad(value.day)}`;
  }

  // Meeus/Jones/Butcher Gregorian computus.
  function easterSunday(year) {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = ((19 * a) + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + (2 * e) + (2 * i) - h - k) % 7;
    const m = Math.floor((a + (11 * h) + (22 * l)) / 451);
    const month = Math.floor((h + l - (7 * m) + 114) / 31);
    const day = ((h + l - (7 * m) + 114) % 31) + 1;
    return { year, month, day };
  }

  function currentDateInTimezone(timezone) {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone || DEFAULT_CONFIG.timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date());
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    return {
      year: Number(values.year),
      month: Number(values.month),
      day: Number(values.day),
    };
  }

  function previewIsAllowed(config) {
    const query = new URLSearchParams(window.location.search);
    return Boolean(
      config.designMode
      || window.Shopify?.designMode
      || query.has('preview_theme_id')
      || document.querySelector('script[data-theme-id][data-theme-published="false"]')
      || window.location.hostname.endsWith('shopifypreview.com')
    );
  }

  function selectedCalendarDate(config) {
    const queryDate = new URLSearchParams(window.location.search).get('hf_campaign_date');
    const previewDate = previewIsAllowed(config) ? parseIsoDate(queryDate || config.previewDate) : null;
    if (previewDate) return { value: previewDate, source: 'preview' };
    return { value: currentDateInTimezone(config.timezone), source: 'clock' };
  }

  function inRecurringWindow(date, startValue, endValue) {
    const start = parseMonthDay(startValue, { month: 1, day: 1 });
    const end = parseMonthDay(endValue, { month: 12, day: 31 });
    const currentKey = monthDayKey(date);
    const startKey = monthDayKey(start);
    const endKey = monthDayKey(end);
    if (startKey <= endKey) return currentKey >= startKey && currentKey <= endKey;
    return currentKey >= startKey || currentKey <= endKey;
  }

  function automaticSeasonState(name, date, seasonConfig) {
    if (name === 'easter') {
      const easter = easterSunday(date.year);
      const leadDays = Math.max(0, Number(seasonConfig.leadDays ?? 56));
      const endOffsetDays = Math.max(0, Number(seasonConfig.endOffsetDays ?? 1));
      return (
        calendarKey(date) >= calendarKey(addDays(easter, -leadDays))
        && calendarKey(date) <= calendarKey(addDays(easter, endOffsetDays))
      );
    }
    return inRecurringWindow(date, seasonConfig.start, seasonConfig.end);
  }

  function computeState(config) {
    const chosenDate = selectedCalendarDate(config);
    const active = {};
    SEASON_NAMES.forEach((name) => {
      const seasonConfig = config.seasons[name] || {};
      const mode = validMode(seasonConfig.mode);
      if (mode === 'always_show') active[name] = true;
      else if (mode === 'always_hide') active[name] = false;
      else active[name] = automaticSeasonState(name, chosenDate.value, seasonConfig);
    });
    return {
      active,
      campaigns: campaignCandidates(config, chosenDate.value, active),
      date: formatIsoDate(chosenDate.value),
      dateSource: chosenDate.source,
      timezone: config.timezone,
      previewAllowed: previewIsAllowed(config),
    };
  }

  function campaignCandidates(config, date, active) {
    const today = calendarKey(date);
    const easter = easterSunday(date.year);
    const juneFirst = new Date(Date.UTC(date.year, 5, 1)).getUTCDay();
    const fathersDay = { year: date.year, month: 6, day: 15 + ((7 - juneFirst) % 7) };
    const candidates = [];
    const within = (event, lead) => today >= calendarKey(addDays(event, -lead)) && today <= calendarKey(event);
    const beforeCutoff = (name) => {
      const cutoff = parseIsoDate(config.campaignCutoffs?.[name]);
      return !cutoff || cutoff.year !== date.year || today <= calendarKey(cutoff);
    };
    if (within({ year: date.year, month: 2, day: 14 }, 44)) candidates.push('valentines');
    if (within(addDays(easter, -21), 42)) candidates.push('mothers');
    if (active.easter) candidates.push('easter');
    if (within(fathersDay, 42)) candidates.push('fathers');
    if (inRecurringWindow(date, '04-01', '08-31')) candidates.push('wedding');
    if (active.halloween) candidates.push('halloween');
    if (active.christmas) {
      if (inRecurringWindow(date, config.christmasLeadStart, config.seasons.christmas.end)) candidates.unshift('christmas');
      else candidates.push('christmas');
    }
    const permitted = candidates.filter(beforeCutoff);
    const override = config.campaignMode;
    if (['valentines', 'mothers', 'easter', 'fathers', 'wedding', 'halloween', 'christmas', 'everyday'].includes(override) && beforeCutoff(override)) {
      return [override, 'everyday'].filter((name, index, list) => list.indexOf(name) === index);
    }
    return [...permitted, 'everyday'];
  }

  function mergeConfiguration(input) {
    const next = structuredCloneSafe(DEFAULT_CONFIG);
    if (input && typeof input === 'object') {
      next.timezone = String(input.timezone || next.timezone);
      next.previewDate = String(input.previewDate || '');
      next.designMode = input.designMode === true;
      next.hideInactiveProducts = input.hideInactiveProducts !== false;
      next.campaignMode = String(input.campaignMode || 'auto');
      next.christmasLeadStart = input.christmasLeadStart || next.christmasLeadStart;
      next.campaignCutoffs = input.campaignCutoffs && typeof input.campaignCutoffs === 'object' ? { ...input.campaignCutoffs } : {};
      SEASON_NAMES.forEach((name) => {
        next.seasons[name] = {
          ...next.seasons[name],
          ...(input.seasons?.[name] || {}),
        };
        next.seasons[name].mode = validMode(next.seasons[name].mode);
      });
    }
    return next;
  }

  function readConfiguration(root = document) {
    const elements = Array.from(root.querySelectorAll?.(CONFIG_SELECTOR) || []);
    if (root.matches?.(CONFIG_SELECTOR)) elements.push(root);
    const element = elements.at(-1);
    if (!element) return null;
    try {
      return JSON.parse(element.textContent || '{}');
    } catch (error) {
      console.warn('[Heartfelt Gifts] Seasonal schedule configuration is invalid.', error);
      return null;
    }
  }

  function seasonForValue(value) {
    const normalized = normalize(value);
    if (normalized.includes('christmas')) return 'christmas';
    if (normalized.includes('halloween')) return 'halloween';
    if (normalized.includes('easter')) return 'easter';
    return '';
  }

  function valuesArray(values) {
    if (Array.isArray(values)) return values;
    return normalize(values).split(',').map((value) => value.trim()).filter(Boolean);
  }

  function isInactiveSeasonalOnlyOccasions(values) {
    const occasions = valuesArray(values).filter(Boolean);
    if (occasions.length === 0) return false;
    let foundSeasonalOccasion = false;
    for (const occasion of occasions) {
      const season = seasonForValue(occasion);
      if (!season) return false;
      foundSeasonalOccasion = true;
      if (state?.active?.[season]) return false;
    }
    return foundSeasonalOccasion;
  }

  function isInactiveSeasonalOnlyTags(tags) {
    const occasions = valuesArray(tags).filter((tag) => normalize(tag).startsWith('gf occasion '));
    return isInactiveSeasonalOnlyOccasions(occasions);
  }

  function isActive(name) {
    const season = seasonForValue(name) || normalize(name);
    return Boolean(state?.active?.[season]);
  }

  function seasonFromLink(anchor) {
    if (!(anchor instanceof HTMLAnchorElement)) return '';
    let url;
    try {
      url = new URL(anchor.href, window.location.origin);
    } catch (_) {
      return '';
    }
    if (!url.pathname.includes('/collections/')) return '';
    return seasonForValue(`${url.pathname} ${url.search}`);
  }

  function discoveryContainer(anchor, scope) {
    if (scope.matches('.hf-browse')) return anchor.closest('.hf-browse-card');
    if (scope.matches('.hf-directory')) return anchor.closest('.hf-directory-card');
    if (scope.matches('.hf-sub')) return anchor.closest('.hf-sub__item');
    if (scope.matches('[data-hf-seasonal-discovery]')) {
      return anchor.closest('[data-hf-seasonal-route], li, article, .card') || anchor;
    }
    return anchor.closest('li') || anchor;
  }

  function rememberOriginalState(element) {
    if (element.hasAttribute(MANAGED_ATTRIBUTE)) return;
    element.setAttribute(MANAGED_ATTRIBUTE, '');
    element.dataset.hfSeasonalOriginalHidden = element.hidden ? 'true' : 'false';
    if (element.hasAttribute('aria-hidden')) {
      element.dataset.hfSeasonalOriginalAriaHidden = element.getAttribute('aria-hidden') || '';
    }
  }

  function setSeasonalVisibility(element, season) {
    if (!(element instanceof HTMLElement) || !season) return;
    rememberOriginalState(element);
    element.dataset.hfSeason = season;
    const hide = !isActive(season);
    element.hidden = hide || element.dataset.hfSeasonalOriginalHidden === 'true';
    if (hide) element.setAttribute('aria-hidden', 'true');
    else if ('hfSeasonalOriginalAriaHidden' in element.dataset) {
      element.setAttribute('aria-hidden', element.dataset.hfSeasonalOriginalAriaHidden);
    } else {
      element.removeAttribute('aria-hidden');
    }
  }

  function applyExplicitSeasonalElements(root) {
    const elements = Array.from(root.querySelectorAll?.('[data-hf-season]') || []);
    if (root.matches?.('[data-hf-season]')) elements.push(root);
    elements.forEach((element) => setSeasonalVisibility(element, seasonForValue(element.dataset.hfSeason)));
  }

  function applyDiscoveryLinks(root) {
    SAFE_DISCOVERY_SCOPES.forEach((selector) => {
      const scopes = Array.from(root.querySelectorAll?.(selector) || []);
      if (root.matches?.(selector)) scopes.push(root);
      scopes.forEach((scope) => {
        scope.querySelectorAll('a[href]').forEach((anchor) => {
          const season = seasonFromLink(anchor);
          if (!season) return;
          setSeasonalVisibility(discoveryContainer(anchor, scope), season);
        });
      });
    });
  }

  function selectedOccasion(finder, values) {
    const selection = values?.occasion;
    return selection?.answered ? normalize(selection.value) : '';
  }

  function enforceFinderControls(finder) {
    const selected = finder.querySelector('select[data-filter="occasion"]')?.value;
    finder.querySelectorAll('[data-gf-option][data-filter="occasion"]').forEach((option) => {
      const season = seasonForValue(option.dataset.value);
      if (!season) return;
      const inactive = !isActive(season) && normalize(option.dataset.value) !== normalize(selected);
      if (inactive) {
        option.hidden = true;
        option.disabled = true;
        option.setAttribute('aria-hidden', 'true');
      } else {
        option.removeAttribute('aria-hidden');
      }
    });
    finder.querySelectorAll('select[data-filter="occasion"] option').forEach((option) => {
      const season = seasonForValue(option.value);
      if (!season) return;
      const inactive = !isActive(season) && normalize(option.value) !== normalize(selected);
      option.hidden = inactive;
      if (inactive) option.disabled = true;
    });
  }

  function reconcileFinder(finder) {
    const select = finder.querySelector('select[data-filter="occasion"]');
    const season = seasonForValue(select?.value);
    if (select && season && !isActive(season) && select.dataset.answered !== 'true' && !select.dataset.selectedLabel) {
      if (typeof finder.clearSelection === 'function') finder.clearSelection('occasion');
      else {
        select.value = '';
        delete select.dataset.answered;
        delete select.dataset.selectedLabel;
      }
      const index = Array.isArray(finder.filters) ? finder.filters.indexOf('occasion') : 1;
      if (typeof finder.pruneAfter === 'function') finder.pruneAfter(index);
      finder.activeFilter = 'occasion';
      if (typeof finder.resetLimit === 'function') finder.resetLimit();
    }
    if (typeof finder.apply === 'function') finder.apply();
    else enforceFinderControls(finder);
  }

  function patchGiftFinder(attempt = 0) {
    const GiftFinder = customElements.get('gift-finder');
    const prototype = GiftFinder?.prototype;
    if (!prototype) return;
    if (prototype.hfSeasonalVisibilityPatched) return;

    // The QA theme loads its complete coverage and taxonomy patches after the
    // base custom element. Wait so this controller wraps their final methods.
    if (!(prototype.hfFullCoveragePatched && prototype.hfTaxonomyGiftTypesPatched) && attempt < 240) {
      setTimeout(() => patchGiftFinder(attempt + 1), 25);
      return;
    }

    Object.defineProperty(prototype, 'hfSeasonalVisibilityPatched', { value: true });

    const originalOptionAvailable = prototype.optionAvailable;
    prototype.optionAvailable = function optionAvailableWithSeason(filter, value) {
      if (filter === 'occasion') {
        const season = seasonForValue(value);
        const selected = this.querySelector('select[data-filter="occasion"]')?.value;
        if (season && !isActive(season) && normalize(value) !== normalize(selected)) return false;
      }
      return originalOptionAvailable.call(this, filter, value);
    };

    const originalRecordMatches = prototype.recordMatches;
    prototype.recordMatches = function recordMatchesWithSeason(record, values) {
      if (!originalRecordMatches.call(this, record, values)) return false;
      if (!configuration.hideInactiveProducts || selectedOccasion(this, values)) return true;
      return !isInactiveSeasonalOnlyOccasions(record?.occasions || []);
    };

    const originalRemoteItemMatches = prototype.remoteItemMatches;
    prototype.remoteItemMatches = function remoteItemMatchesWithSeason(item, values) {
      if (!originalRemoteItemMatches.call(this, item, values)) return false;
      if (!configuration.hideInactiveProducts || selectedOccasion(this, values)) return true;
      const card = typeof this.remoteItemCard === 'function' ? this.remoteItemCard(item) : null;
      return !isInactiveSeasonalOnlyTags(card?.dataset.tags || '');
    };

    const originalSyncChoices = prototype.syncChoices;
    prototype.syncChoices = function syncChoicesWithSeason() {
      originalSyncChoices.call(this);
      enforceFinderControls(this);
    };

    document.querySelectorAll('gift-finder').forEach(reconcileFinder);
  }

  function updateDocumentState() {
    const root = document.documentElement;
    SEASON_NAMES.forEach((name) => {
      root.classList.toggle(`hf-season-${name}-active`, isActive(name));
      root.classList.toggle(`hf-season-${name}-inactive`, !isActive(name));
    });
    root.classList.add('hf-seasonal-schedule-ready');
    root.dataset.hfSeasonalDate = state.date;
    root.dataset.hfSeasonalDateSource = state.dateSource;
  }

  function apply(root = document) {
    if (!state) return;
    applyExplicitSeasonalElements(root);
    applyDiscoveryLinks(root);
    const finders = Array.from(root.querySelectorAll?.('gift-finder') || []);
    if (root.matches?.('gift-finder')) finders.push(root);
    finders.forEach(enforceFinderControls);
  }

  function isElementNode(node) {
    return node?.nodeType === 1;
  }

  function queueApplyRoot(root) {
    if (!isElementNode(root)) return;

    for (const pendingRoot of pendingApplyRoots) {
      if (pendingRoot === root || pendingRoot.contains(root)) return;
    }
    for (const pendingRoot of pendingApplyRoots) {
      if (root.contains(pendingRoot)) pendingApplyRoots.delete(pendingRoot);
    }

    pendingApplyRoots.add(root);
    if (applyFrame) return;
    applyFrame = window.requestAnimationFrame(flushApplyRoots);
  }

  function flushApplyRoots() {
    applyFrame = 0;
    const roots = Array.from(pendingApplyRoots);
    pendingApplyRoots.clear();
    roots.forEach((root) => apply(root));
  }

  function rootForAddedElement(element) {
    const containingRoot = element.parentElement?.closest?.(DISCOVERY_ROOT_SELECTOR);
    if (containingRoot) return containingRoot;
    if (
      element.matches?.(MANAGED_ROOT_SELECTOR)
      || element.querySelector?.(MANAGED_ROOT_SELECTOR)
    ) return element;
    return null;
  }

  function scheduleMutationRoots(records) {
    records.forEach((record) => {
      if (record.type !== 'childList') return;
      Array.from(record.addedNodes || []).forEach((node) => {
        if (!isElementNode(node)) return;
        const root = rootForAddedElement(node);
        if (root) queueApplyRoot(root);
      });
    });
  }

  function cancelPendingApply() {
    if (applyFrame) window.cancelAnimationFrame(applyFrame);
    applyFrame = 0;
    pendingApplyRoots.clear();
  }

  function observeDocument() {
    observer?.observe(document.documentElement, { childList: true, subtree: true });
  }

  function setConfiguration(input, eventName = 'hf:seasonal-schedule-ready') {
    cancelPendingApply();
    const activeObserver = observer;
    activeObserver?.disconnect();
    configuration = mergeConfiguration(input);
    state = computeState(configuration);
    updateDocumentState();
    apply(document);
    document.querySelectorAll('gift-finder').forEach(reconcileFinder);
    if (activeObserver) observeDocument();
    document.dispatchEvent(new CustomEvent(eventName, { detail: publicState() }));
  }

  function publicState() {
    return {
      active: { ...state.active },
      campaigns: [...state.campaigns],
      date: state.date,
      dateSource: state.dateSource,
      timezone: state.timezone,
      previewAllowed: state.previewAllowed,
      hideInactiveProducts: configuration.hideInactiveProducts,
    };
  }

  window.HFSeasonalSchedule = {
    apply,
    computeState: (input) => computeState(mergeConfiguration(input)),
    easterSunday,
    get state() { return state ? publicState() : null; },
    isActive,
    isInactiveSeasonalOnlyOccasions,
    isInactiveSeasonalOnlyTags,
    seasonForValue,
    setConfiguration: (input) => setConfiguration(input, 'hf:seasonal-schedule-change'),
  };

  const initialConfiguration = readConfiguration(document);
  setConfiguration(initialConfiguration || DEFAULT_CONFIG);

  if ('MutationObserver' in window) {
    observer = new MutationObserver(scheduleMutationRoots);
    observeDocument();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const nextConfiguration = readConfiguration(event.target);
    if (nextConfiguration) setConfiguration(nextConfiguration, 'hf:seasonal-schedule-change');
    else queueApplyRoot(event.target);
  });

  const refreshCalendar = () => {
    if (document.visibilityState === 'hidden') return;
    const next = computeState(configuration);
    if (next.date !== state.date || next.dateSource !== state.dateSource) setConfiguration(configuration, 'hf:seasonal-schedule-change');
  };
  window.addEventListener('pageshow', refreshCalendar);
  document.addEventListener('visibilitychange', refreshCalendar);
  window.setInterval(refreshCalendar, 60000);

  customElements.whenDefined('gift-finder')
    .then(() => patchGiftFinder())
    .catch((error) => {
      console.error('[Heartfelt Gifts] Seasonal Gift Finder controls could not initialise.', error);
    });
})();
